import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import dateToLocaleString from 'Utils/DateToLocaleString';
import SideBarContainer from 'Containers/SideBarContainer';
import Chip from 'Components/Chip';
import useTranslation from 'Hooks/useTranslation';
import DraftChip from 'Components/DraftChip';
import TabPage from 'Components/TabPage';
import { useSelector } from 'react-redux';
import { userSelector } from 'Redux/LoggedUserSlice';
import { ROLE_ADMINISTRATOR } from 'Constants/General';
import { CheckOutlined } from '@mui/icons-material';
import Information from './Pages/Information';
import Lessons from './Pages/Lessons';
import Documents from './Pages/Documents';
import Members from './Pages/Members';
import Dates from './Pages/Dates';
import Evaluations from './Pages/Evaluations';
import Certificate from './Pages/Certificate';
import style from './style';
import CourseThumbnail from './Components/CourseThumbnail';
import useCourseDetails from './Hooks/useCourseDetails';
import CourseEditButton from './Components/CourseEditButton';
import { s3 } from 'Services/AWS/s3';

const {
  ContainerCourse,
  ContainerHeader,
  HeaderContent,
  HeaderInfo,
  HeaderTitle,
  HeaderSubTitle,
  checkIcon,
  LastUpdatedContainer,
  LastUpdated,
  lastUpdatedLabel,
  lastUpdatedText,
} = style;

export default function Course() {
  const { t2 } = useTranslation('CourseDetails');
  const location = useLocation();
  // El id viene de la URL: la pantalla sobrevive a un refresh y es compartible.
  const { courseId } = useParams();
  // El color del chip es estético y solo lo conoce el listado de origen.
  const colorCategory = location?.state?.colorCategory;
  const { id, roleId } = useSelector(userSelector);

  const { course, loading } = useCourseDetails({ courseId });

  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);

  const thumbnailKey = course?.thumbnailKey;

  // Firma la imagen del curso en cuanto llega la key del fetch.
  useEffect(() => {
    if (!thumbnailKey) return;

    setThumbnailLoading(true);
    s3.getSignedUrl(thumbnailKey)
      .then(url => {
        setThumbnailUrl(url?.signedUrl);
      })
      .finally(() => {
        setThumbnailLoading(false);
      });
  }, [thumbnailKey]);

  const tabsTitle = [
    t2('BasicInfo'),
    t2('Lessons'),
    t2('Documents'),
    t2('Members'),
    t2('Dates'),
    t2('Evaluations'),
    t2('Certification'),
  ];

  const pages = [
    <Information key="information" course={course} />,
    <Lessons key="lessons" course={course} />,
    <Documents key="documents" course={course} />,
    <Members key="members" course={course} />,
    <Dates key="dates" course={course} />,
    <Evaluations key="evaluations" course={course} />,
    <Certificate key="certificate" course={course} />,
  ];

  return (
    <SideBarContainer>
      <ContainerCourse>
        <ContainerHeader>
          <HeaderContent>
            <CourseThumbnail image={thumbnailUrl} loading={loading || thumbnailLoading} />
            <HeaderInfo>
              <HeaderTitle>
                <div>{course?.name}</div>
                <Chip
                  style={{
                    border: 'none',
                    background: colorCategory,
                    color: 'white',
                  }}
                >
                  {null}
                  <h4>{course?.categoryName}</h4>
                </Chip>
                <DraftChip visible={!!course?.isDraft} />
              </HeaderTitle>
              <HeaderSubTitle>
                <div>
                  {t2('By')} {course?.creatorName}
                </div>
                <LastUpdatedContainer>
                  <CheckOutlined style={checkIcon} />
                  <LastUpdated>
                    <div style={lastUpdatedLabel}>{t2('LastUpdated')}:</div>
                    {/* dateToLocaleString lanza sin fecha, y aquí no hay ninguna hasta que responde el fetch. */}
                    <div style={lastUpdatedText}>{course?.updatedAt ? dateToLocaleString(course.updatedAt) : ''}</div>
                  </LastUpdated>
                </LastUpdatedContainer>
              </HeaderSubTitle>
            </HeaderInfo>
            {course && (
              <CourseEditButton
                courseId={course.id}
                visible={course.userCreatedId === id || roleId === ROLE_ADMINISTRATOR}
              />
            )}
          </HeaderContent>
        </ContainerHeader>
        <TabPage tabsTitle={tabsTitle} pages={pages} />
      </ContainerCourse>
    </SideBarContainer>
  );
}
